import { useState, useCallback, useRef } from "react";

import api from "@services/api";
import { useToast } from "@hooks/useToast";
import useTranslation from "src/translations/useTranslation";
import { ToastMessages } from "@shared/languages/interfaces";

import { TravelAnalysisAlertRow, TravelAnalysisFilter } from "../types";

const USE_MOCK_DATA = false; // TODO: revert to `process.env.NODE_ENV === "development"` when needed

const ALERTS_PAGE_SIZE = 20;

/** Mock alert rows for visual testing during development. */
const MOCK_ALERTS: TravelAnalysisAlertRow[] = [
  {
    id: "ma-1",
    travelId: "mt-1",
    numDoc: "NF-001",
    alertType: { id: "136db83f-46a7-4837-970c-21e726fc978f", description: "Instabilidade de conexão GPRS" },
    vehicle: { id: "v1", code: "VH-001", description: "Volvo FH 540" },
    origin: { latitude: "-23.5505", longitude: "-46.6333", name: "São Paulo - SP" },
    destination: { latitude: "-22.9068", longitude: "-43.1729", name: "Rio de Janeiro - RJ" },
    metadata: null,
    registrationDate: "2026-04-20T14:30:00.000Z"
  },
  {
    id: "ma-2",
    travelId: "mt-2",
    numDoc: "NF-002",
    alertType: { id: "e2f32d7a-3076-4ba5-879e-f0333db7c232", description: "Informações de CAN travadas (completas ou parciais)" },
    vehicle: { id: "v2", code: "VH-015", description: "Scania R450" },
    origin: { latitude: "-22.9068", longitude: "-43.1729", name: "Rio de Janeiro - RJ" },
    destination: { latitude: "-19.9167", longitude: "-43.9345", name: "Belo Horizonte - MG" },
    metadata: null,
    registrationDate: "2026-04-19T09:15:00.000Z"
  },
  {
    id: "ma-3",
    travelId: "mt-3",
    numDoc: null,
    alertType: { id: "2f6349ac-719a-4b35-a161-45f513bb91e2", description: "Falha de posicionamento GPS" },
    vehicle: { id: "v3", code: "VH-042", description: "Mercedes Actros 2651" },
    origin: { latitude: "-25.4284", longitude: "-49.2733", name: "Curitiba - PR" },
    destination: null,
    metadata: null,
    registrationDate: "2026-04-18T16:45:00.000Z"
  },
  {
    id: "ma-4",
    travelId: "mt-4",
    numDoc: "NF-004",
    alertType: { id: "136db83f-46a7-4837-970c-21e726fc978f", description: "Instabilidade de conexão GPRS" },
    vehicle: { id: "v4", code: "VH-007", description: "DAF XF 530" },
    origin: { latitude: "-23.1896", longitude: "-46.8844", name: "Jundiaí - SP" },
    destination: { latitude: "-23.9608", longitude: "-46.3336", name: "Santos - SP" },
    metadata: null,
    registrationDate: "2026-04-17T11:20:00.000Z"
  },
  {
    id: "ma-5",
    travelId: "mt-5",
    numDoc: "NF-005",
    alertType: { id: "909443c5-7b7d-40a2-ae79-0534b90a8628", description: "Déficit de comandos de hardware enviados" },
    vehicle: { id: "v5", code: "VH-023", description: "Iveco S-Way 570" },
    origin: { latitude: "-20.3155", longitude: "-40.3128", name: "Vitória - ES" },
    destination: { latitude: "-22.9068", longitude: "-43.1729", name: "Rio de Janeiro - RJ" },
    metadata: null,
    registrationDate: "2026-04-16T08:00:00.000Z"
  },
  {
    id: "ma-6",
    travelId: "mt-6",
    numDoc: "NF-006",
    alertType: { id: "0445b44e-9081-436e-904b-62bacdf2ec57", description: "Déficit de comandos de sensor ativo (IDLE elevado)" },
    vehicle: { id: "v1", code: "VH-001", description: "Volvo FH 540" },
    origin: { latitude: "-23.5505", longitude: "-46.6333", name: "São Paulo - SP" },
    destination: { latitude: "-25.4284", longitude: "-49.2733", name: "Curitiba - PR" },
    metadata: null,
    registrationDate: "2026-04-15T20:10:00.000Z"
  }
];

/** Extra filters for grouped chart drilldowns. */
export interface DrilldownExtraFilters {
  originName?: string;
  regionalName?: string;
  dayFilter?: string;
}

/** Selected alert type for filtered drilldown. */
interface SelectedAlertType {
  id: string;
  description: string;
}

/** Return shape of the `useTravelAnalysisAlerts` hook. */
interface UseTravelAnalysisAlertsReturn {
  open: boolean;
  alerts: TravelAnalysisAlertRow[];
  alertsLoading: boolean;
  totalCount: number;
  selectedAlertType: SelectedAlertType | null;
  openDrilldown: (alertTypeId?: string, alertDescription?: string, extra?: DrilldownExtraFilters) => void;
  closeDrilldown: () => void;
  loadMore: () => void;
}

/**
 * Manages state and API calls for the alerts drilldown modal.
 * Receives the current dashboard filter so alert queries use the same date/fleet scope.
 */
const useTravelAnalysisAlerts = (currentFilter: TravelAnalysisFilter | null): UseTravelAnalysisAlertsReturn => {

  const { addToast } = useToast();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState<TravelAnalysisAlertRow[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedAlertType, setSelectedAlertType] = useState<SelectedAlertType | null>(null);

  // Ref to track selected alert type inside async callbacks without stale closures
  const alertTypeRef = useRef<SelectedAlertType | null>(null);
  const extraFiltersRef = useRef<DrilldownExtraFilters | undefined>(undefined);

  /** Fetch alerts from the API (or return mock data in dev). */
  const fetchAlerts = useCallback(async (alertTypeId?: string, offset = 0, append = false, extra?: DrilldownExtraFilters) => {

    if (USE_MOCK_DATA) {
      setAlertsLoading(true);
      setTimeout(() => {
        const filtered = alertTypeId
          ? MOCK_ALERTS.filter((a) => a.alertType.id === alertTypeId)
          : MOCK_ALERTS;

        setAlerts((prev) => (append ? [...prev, ...filtered] : filtered));
        setTotalCount(filtered.length);
        setAlertsLoading(false);
      }, 500);

      return;
    }

    if (!currentFilter) return;

    setAlertsLoading(true);

    try {

      const payload = {
        ...currentFilter,
        alertTypeId,
        originName: extra?.originName || undefined,
        regionalName: extra?.regionalName || undefined,
        dayFilter: extra?.dayFilter || undefined,
        offset,
        limit: ALERTS_PAGE_SIZE
      };

      const { data } = await api.post("travels/analysis/alerts", payload);

      if (data.status === "success") {
        setAlerts((prev) => (append ? [...prev, ...data.result] : data.result));
        setTotalCount(data.pagination?.totalCount ?? data.result.length);
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }

    } catch (err) {
      const message = err?.response?.data?.message || t(ToastMessages.connectionNotEstablished);

      addToast({ type: "error", title: t(ToastMessages.error), description: message });
    } finally {
      setAlertsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilter]);

  /** Open the drilldown modal, optionally filtered by alert type and/or extra filters. */
  const openDrilldown = useCallback((alertTypeId?: string, alertDescription?: string, extra?: DrilldownExtraFilters) => {

    const type = alertTypeId ? { id: alertTypeId, description: alertDescription || "" } : null;

    alertTypeRef.current = type;
    extraFiltersRef.current = extra;
    setSelectedAlertType(type);
    setAlerts([]);
    setTotalCount(0);
    setOpen(true);
    fetchAlerts(alertTypeId, 0, false, extra);
  }, [fetchAlerts]);

  /** Close the drilldown modal and reset state. */
  const closeDrilldown = useCallback(() => {
    setOpen(false);
    setAlerts([]);
    setSelectedAlertType(null);
    setTotalCount(0);
    alertTypeRef.current = null;
    extraFiltersRef.current = undefined;
  }, []);

  /** Load the next page of alerts (append to existing list). */
  const loadMore = useCallback(() => {
    fetchAlerts(alertTypeRef.current?.id, alerts.length, true, extraFiltersRef.current);
  }, [fetchAlerts, alerts.length]);

  return { open, alerts, alertsLoading, totalCount, selectedAlertType, openDrilldown, closeDrilldown, loadMore };
};

export default useTravelAnalysisAlerts;
